import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const PACKAGES = [
  { id: 'start',   nameKey: 'pkg.start.name',   investment: 2700, profit: 29, badge: null,       highlight: false },
  { id: 'balance', nameKey: 'pkg.balance.name',  investment: 5800, profit: 37, badge: 'popular',  highlight: true  },
  { id: 'premium', nameKey: 'pkg.premium.name',  investment: 9800, profit: 49, badge: 'max',       highlight: false },
];

const FEATURES_MAP: Record<string, string[]> = {
  start:   ['pkg.f.threshold', 'pkg.f.profit', 'pkg.f.report.monthly', 'pkg.f.support.reg', 'pkg.f.account'],
  balance: ['pkg.f.threshold', 'pkg.f.profit', 'pkg.f.report.weekly', 'pkg.f.support.prio', 'pkg.f.account', 'pkg.f.analytics'],
  premium: ['pkg.f.threshold', 'pkg.f.profit', 'pkg.f.report.daily', 'pkg.f.support.vip', 'pkg.f.account', 'pkg.f.analytics', 'pkg.f.manager'],
};

const useInView = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
};

const InvestmentPackages = () => {
  const { t } = useTranslation();
  const { ref, visible } = useInView();

  const scrollToPackages = () => {
    const el = document.getElementById('packages');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const getBadgeLabel = (badge: string | null) => {
    if (badge === 'popular') return t('pkg.popular');
    if (badge === 'max') return t('pkg.max');
    return null;
  };

  const getFeatures = (pkg: typeof PACKAGES[0]) =>
    FEATURES_MAP[pkg.id].map(key =>
      t(key, { amount: pkg.investment.toLocaleString(), pct: pkg.profit })
    );

  return (
    <section id="packages" className="py-20 md:py-28 bg-[#111418]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-briefcase-line text-green-400 text-sm"></i>
            <span className="text-green-400 text-xs font-semibold uppercase tracking-wide">{t('pkg.badge')}</span>
          </div>
          <h2 className="text-3xl md:text-4xl xl:text-5xl font-black text-white mb-4">
            {t('pkg.title_p1')} <span className="text-green-400">{t('pkg.title_hl')}</span> {t('pkg.title_p2')}
          </h2>
          <p className="text-white/50 text-base max-w-xl mx-auto">
            {t('pkg.subtitle')}
          </p>
        </div>

        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {PACKAGES.map((pkg, i) => (
            <div
              key={pkg.id}
              className={`relative rounded-2xl transition-all duration-300 ${
                pkg.highlight
                  ? 'bg-white border-2 border-green-400 shadow-2xl scale-105'
                  : 'bg-white border border-gray-100'
              }`}
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? (pkg.highlight ? 'scale(1.05) translateY(0)' : 'translateY(0)') : 'translateY(32px)',
                transition: `opacity 0.5s ease ${i * 0.12}s, transform 0.5s ease ${i * 0.12}s`,
              }}
            >
              {pkg.badge && (
                <div className={`absolute -top-3.5 left-1/2 -translate-x-1/2 px-5 py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                  pkg.highlight ? 'bg-green-500 text-white' : 'bg-amber-400 text-amber-900'
                }`}>
                  {getBadgeLabel(pkg.badge)}
                </div>
              )}

              <div className="p-7">
                {/* Header */}
                <div className="mb-6">
                  <h3 className="text-gray-900 font-bold text-xl mb-1">{t(pkg.nameKey)}</h3>
                  <div className="text-gray-400 text-sm">{t('pkg.seasonal')}</div>
                </div>

                {/* Investment */}
                <div className="mb-4">
                  <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1">{t('pkg.investment')}</div>
                  <div className="text-gray-900 font-black text-4xl">€{pkg.investment.toLocaleString()}</div>
                </div>

                {/* Duration badge */}
                <div className="inline-flex items-center gap-1.5 bg-green-50 border border-green-200 rounded-full px-3 py-1 mb-4">
                  <i className="ri-time-line text-green-500 text-xs"></i>
                  <span className="text-green-700 text-xs font-bold">{t('pkg.duration')} 2 {t('pkg.days')}</span>
                </div>

                <div className="border-t border-gray-100 my-5"></div>

                {/* Profit */}
                <div className="mb-6">
                  <div className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-1">{t('pkg.yield')}</div>
                  <div className={`font-black text-6xl ${pkg.highlight ? 'text-green-500' : 'text-green-600'}`}>
                    {pkg.profit}
                    <span className="text-3xl">%</span>
                  </div>
                  <div className="text-gray-400 text-sm mt-1">
                    {t('pkg.profit')} <strong className="text-gray-700">€{Math.round(pkg.investment * pkg.profit / 100).toLocaleString()}</strong>
                  </div>
                </div>

                {/* Features */}
                <ul className="space-y-2.5 mb-7">
                  {getFeatures(pkg).map((f, fi) => (
                    <li key={fi} className="flex items-center gap-2.5 text-sm text-gray-600">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-line text-green-500"></i>
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={scrollToPackages}
                  className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap ${
                    pkg.highlight
                      ? 'bg-green-500 hover:bg-green-400 text-white'
                      : 'border-2 border-green-500 text-green-600 hover:bg-green-500 hover:text-white'
                  }`}
                >
                  {t('pkg.cta')}
                </button>
              </div>
            </div>
          ))}
        </div>

        <p className="text-center text-white/30 text-xs mt-8">
          {t('pkg.disclaimer')}
        </p>
      </div>
    </section>
  );
};

export default InvestmentPackages;
